import { useState, useEffect, useCallback, useRef } from 'react';
import { MessageCircle } from 'lucide-react';
import { useAuthContext } from '@/hooks/useAuthContext';
import { 
  useChartingQueue, 
  useAssignToSelf, 
  useAssignToUser,
  useMarkCompleted, 
  useRequireExceptionApproval,
  useChartingTeamMembers,
  useUpdateLineItemStatus,
  useChartingStatistics,
} from '@/hooks/useCharting';
import { Card, CardHeader, CardTitle, CardContent, Button, StatusPill, Modal, Sheet } from '@/components/ui';
import { LoadingSpinner, ErrorMessage } from '@/components/common';
import { QueueFilters, LineItemDetail } from '@/components/charting';
import type { ChartingQueueFilters, ChartingQueueItem } from '@/hooks/useCharting';
import type { ChartingStatus } from '@/types';

const FILTERS_STORAGE_KEY = 'chartingQueue.filters';

const DEFAULT_STATUSES: ChartingStatus[] = ['open', 'in_progress', 'approval_required_by_exception', 'revision_required_by_sales'];

// Load filters from localStorage
const loadSavedFilters = (): ChartingQueueFilters => {
  try {
    const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Strip legacy keys and reset offset
      const { mediaType: _legacyMedia, chartingStatus: _legacyStatus, ...rest } = parsed;
      return { ...rest, chartingStatuses: rest.chartingStatuses || DEFAULT_STATUSES, offset: 0 };
    }
  } catch (error) {
    console.error('Failed to load saved filters:', error);
  }
  // Default filters
  return {
    sortBy: 'created' as const,
    sortOrder: 'asc' as const,
    chartingStatuses: DEFAULT_STATUSES,
    limit: 50,
    offset: 0,
  };
};

// Save filters to localStorage
const saveFilters = (filters: ChartingQueueFilters) => {
  try {
    // Don't save offset (always start at page 1)
    const { offset: _offset, ...filtersToSave } = filters;
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave));
  } catch (error) {
    console.error('Failed to save filters:', error);
  }
};

type QuickFilter = 'unassigned' | 'assigned_to_you' | 'in_progress' | 'overdue' | 'exceptions_required';

export default function ChartingQueue() {
  const { user } = useAuthContext();
  const [filters, setFilters] = useState<ChartingQueueFilters>(loadSavedFilters);
  const [activeQuickFilter, setActiveQuickFilter] = useState<QuickFilter | null>(null);
  const [selectedItem, setSelectedItem] = useState<ChartingQueueItem | null>(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLineItemId, setSelectedLineItemId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'complete' | 'exception' | null>(null);
  const [comments, setComments] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isFetching, error } = useChartingQueue(filters);
  const { data: teamMembers, isLoading: isLoadingTeam } = useChartingTeamMembers();
  const { data: stats } = useChartingStatistics();
  const assignToSelf = useAssignToSelf();
  const assignToUser = useAssignToUser();
  const markCompleted = useMarkCompleted();
  const requireException = useRequireExceptionApproval();
  const updateLineItemStatus = useUpdateLineItemStatus();

  const isManager = user?.role === 'charting_manager';

  // Use server-side statistics for accurate counts across all pages, fall back to current page
  const allItems = data?.items || [];
  const quickFilterCounts = {
    unassigned: stats?.totalUnassigned ?? allItems.filter(item => !item.assignedChartingUserId).length,
    assigned_to_you: stats?.myAssigned ?? allItems.filter(item => item.assignedChartingUserId === user?.id).length,
    in_progress: stats?.totalInProgress ?? allItems.filter(item => item.chartingStatus === 'in_progress').length,
    overdue: stats?.totalOverdue ?? allItems.filter(item => item.daysUntilExpiration !== undefined && item.daysUntilExpiration <= 0).length,
    exceptions_required: stats?.totalExceptionsRequired ?? allItems.filter(item => item.chartingStatus === 'approval_required_by_exception').length,
  };

  const handleQuickFilter = useCallback((filter: QuickFilter) => {
    setActiveQuickFilter(prev => {
      const isDeactivating = prev === filter;
      // Build new filters: start from current but clear quick-filter-related keys
      setFilters(currentFilters => {
        const base = { ...currentFilters, unassignedOnly: undefined, myAssignments: undefined, chartingStatus: undefined, chartingStatuses: DEFAULT_STATUSES, expiringWithinDays: undefined, includeExpired: undefined, offset: 0 };
        if (isDeactivating) {
          saveFilters(base);
          return base;
        }
        let newFilters: ChartingQueueFilters;
        switch (filter) {
          case 'unassigned':
            newFilters = { ...base, unassignedOnly: true };
            break;
          case 'assigned_to_you':
            newFilters = { ...base, myAssignments: true };
            break;
          case 'in_progress':
            newFilters = { ...base, chartingStatuses: ['in_progress'] };
            break;
          case 'overdue':
            // Overdue = expiring within 0 days (today or past) — must include expired to see them
            newFilters = { ...base, expiringWithinDays: 0, includeExpired: true };
            break;
          case 'exceptions_required':
            newFilters = { ...base, chartingStatuses: ['approval_required_by_exception'] };
            break;
          default:
            newFilters = base;
        }
        saveFilters(newFilters);
        return newFilters;
      });
      return isDeactivating ? null : filter;
    });
  }, []);

  // Memoize callbacks to prevent QueueFilters re-renders
  const handleFilterChange = useCallback((key: keyof ChartingQueueFilters, value: ChartingQueueFilters[keyof ChartingQueueFilters]) => {
    // Clear quick filter when user manually changes related filter keys
    const quickFilterKeys: (keyof ChartingQueueFilters)[] = ['unassignedOnly', 'myAssignments', 'chartingStatus', 'chartingStatuses', 'expiringWithinDays'];
    if (quickFilterKeys.includes(key)) {
      setActiveQuickFilter(null);
    }
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value, offset: 0 };
      saveFilters(newFilters);
      return newFilters;
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    const defaultFilters: ChartingQueueFilters = {
      sortBy: 'created' as const,
      sortOrder: 'asc' as const,
      chartingStatuses: DEFAULT_STATUSES,
      limit: 50,
      offset: 0,
    };
    setFilters(defaultFilters);
    setActiveQuickFilter(null);
    saveFilters(defaultFilters);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAssignToSelf = async (item: ChartingQueueItem) => {
    try {
      // Line item assignment only
      if (item.id && !item.assignedChartingUserId) {
        await assignToSelf.mutateAsync(item.id);
      }
    } catch (error) {
      console.error('Failed to assign:', error);
    }
  };

  const handleOpenAssignModal = (item: ChartingQueueItem) => {
    setSelectedItem(item);
    setSelectedUserId('');
    setShowAssignModal(true);
  };

  const handleAssignToUser = async () => {
    if (!selectedItem || !selectedUserId) return;

    try {
      // Assign line item only (charting status)
      await assignToUser.mutateAsync({
        lineItemId: selectedItem.id,
        targetUserId: selectedUserId,
      });
      setShowAssignModal(false);
      setSelectedItem(null);
      setSelectedUserId('');
      setSendNotification(true);
    } catch (error) {
      console.error('Failed to assign to user:', error);
    }
  };

  const handleOpenActionModal = (item: ChartingQueueItem, action: 'complete' | 'exception') => {
    setSelectedItem(item);
    setActionType(action);
    setComments('');
    setShowActionModal(true);
  };

  const handleOpenDetailModal = (lineItemId: string) => {
    setSelectedLineItemId(lineItemId);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setSelectedLineItemId(null);
    setShowDetailModal(false);
  };

  const handleStatusChange = async (item: ChartingQueueItem, newStatus: ChartingStatus, comment?: string) => {
    try {
      await updateLineItemStatus.mutateAsync({
        lineItemId: item.id,
        chartingStatus: newStatus,
        comment,
      });
    } catch (error) {
      console.error('Failed to update status:', error);
      throw error; // Re-throw to let StatusPill handle the error display
    }
  };

  const handleSubmitAction = async () => {
    if (!selectedItem || !actionType) return;

    try {
      if (actionType === 'complete') {
        await markCompleted.mutateAsync({
          lineItemId: selectedItem.id,
          ...(comments && { comments }),
        });
      } else {
        if (!comments.trim()) {
          alert('Reason is required for exception approval');
          return;
        }
        await requireException.mutateAsync({
          lineItemId: selectedItem.id,
          reason: comments,
        });
      }
      setShowActionModal(false);
      setSelectedItem(null);
      setComments('');
    } catch (error) {
      console.error('Failed to submit action:', error);
    }
  };

  const getExpirationColor = (days: number | undefined) => {
    if (days === undefined) return 'text-neutral-600';
    if (days < 0) return 'text-accent-red font-semibold';
    if (days === 0) return 'text-accent-orange font-semibold';
    if (days <= 2) return 'text-yellow-600';
    return 'text-neutral-600';
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message="Failed to load charting queue" />
      </div>
    );
  }

  const items = data?.items || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6 px-4 py-6 sm:p-6">
      {/* OUTFRONT Command Center Header */}
      <div>
        <h1 className="text-2xl sm:text-display-lg font-display text-outfront-charcoal">Line Item Work Queue</h1>
        <p className="mt-2 text-lg text-neutral-600">
          Command center for processing line items and managing charting assignments
        </p>
      </div>

      {/* Quick Filter Pills */}
      <div className="flex flex-wrap gap-3">
        {([
          { key: 'unassigned' as QuickFilter, label: 'Unassigned', count: quickFilterCounts.unassigned, color: 'red' as const },
          { key: 'assigned_to_you' as QuickFilter, label: 'Assigned to You', count: quickFilterCounts.assigned_to_you, color: 'purple' as const },
          { key: 'in_progress' as QuickFilter, label: 'In Progress', count: quickFilterCounts.in_progress, color: 'teal' as const },
          { key: 'overdue' as QuickFilter, label: 'Overdue', count: quickFilterCounts.overdue, color: 'orange' as const },
          { key: 'exceptions_required' as QuickFilter, label: 'Exceptions Required', count: quickFilterCounts.exceptions_required, color: 'yellow' as const },
        ]).map(({ key, label, count, color }) => {
          const isActive = activeQuickFilter === key;
          const colorStyles = {
            red: { bg: 'bg-red-50', activeBg: 'bg-red-100 ring-2 ring-accent-red', text: 'text-accent-red', ring: 'hover:ring-red-300' },
            purple: { bg: 'bg-purple-50', activeBg: 'bg-purple-100 ring-2 ring-outfront-purple', text: 'text-outfront-purple', ring: 'hover:ring-purple-300' },
            teal: { bg: 'bg-teal-50', activeBg: 'bg-teal-100 ring-2 ring-accent-teal', text: 'text-accent-teal', ring: 'hover:ring-teal-300' },
            orange: { bg: 'bg-orange-50', activeBg: 'bg-orange-100 ring-2 ring-accent-orange', text: 'text-accent-orange', ring: 'hover:ring-orange-300' },
            yellow: { bg: 'bg-yellow-50', activeBg: 'bg-yellow-100 ring-2 ring-yellow-500', text: 'text-yellow-700', ring: 'hover:ring-yellow-300' },
          } as const;
          const c = colorStyles[color];
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleQuickFilter(key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                isActive ? c.activeBg : `${c.bg} ${c.ring}`
              }`}
              aria-pressed={isActive}
            >
              <span className={`text-lg font-bold ${c.text}`}>{count}</span>
              <span className={isActive ? c.text : 'text-outfront-charcoal'}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* Filters — desktop: inline card, mobile: slide-out sheet */}
      <div className="hidden md:block">
        <QueueFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />
      </div>
      <div className="md:hidden">
        <Button
          variant="outline"
          onClick={() => setShowMobileFilters(true)}
          className="w-full"
        >
          Filters
          {(() => {
            const activeCount = [
              filters.chartingStatuses && filters.chartingStatuses.length > 0,
              filters.markets && filters.markets.length > 0,
              filters.mediaTypes && filters.mediaTypes.length > 0,
              filters.unassignedOnly,
              filters.myAssignments,
            ].filter(Boolean).length;
            return activeCount > 0 ? ` (${activeCount})` : '';
          })()}
        </Button>
        <Sheet
          isOpen={showMobileFilters}
          onClose={() => setShowMobileFilters(false)}
          title="Filters"
          side="right"
        >
          <QueueFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />
        </Sheet>
      </div>

      {/* Queue Items */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-outfront-charcoal">
              Hold Request Queue
              {pagination && pagination.total > 0 && (
                <span className="ml-2 text-sm font-normal text-neutral-400">
                  {(filters.offset || 0) + 1}-{Math.min((filters.offset || 0) + (filters.limit || 50), pagination.total)} of {pagination.total}
                </span>
              )}
              {pagination && pagination.total === 0 && (
                <span className="ml-2 text-sm font-normal text-neutral-400">(0)</span>
              )}
              {isFetching && (
                <span className="ml-2 inline-block text-xs font-normal text-neutral-400">Updating...</span>
              )}
            </CardTitle>
            <div className="flex items-center gap-4 text-xs text-neutral-400">
              <button
                type="button"
                onClick={() => handleQuickFilter('assigned_to_you')}
                className={`flex items-center gap-1 cursor-pointer rounded px-2 py-1 transition-colors ${
                  activeQuickFilter === 'assigned_to_you' ? 'bg-outfront-purple-light text-outfront-purple' : 'hover:bg-neutral-100'
                }`}
              >
                <div className="w-3 h-3 bg-outfront-purple-light border-l-2 border-outfront-purple"></div>
                <span>Your assignments ({quickFilterCounts.assigned_to_you})</span>
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="min-h-[500px]">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[500px] text-center">
              <svg className="w-16 h-16 text-neutral-200 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              <p className="text-neutral-400 text-lg font-medium mb-1">No queue items found</p>
              <p className="text-neutral-400 text-sm">Try adjusting your filters to see more results</p>
            </div>
          ) : (
            <div>
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 tracking-wider">
                      Created
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 tracking-wider">
                      Request #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 tracking-wider">
                      Advertiser
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 tracking-wider">
                      Line Item
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-400 tracking-wider">
                      Qty / Spots
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 tracking-wider">
                      Expiration
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 tracking-wider">
                      Assigned To
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-400 tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {items.map((item: ChartingQueueItem) => {
                    const isAssignedToCurrentUser = 
                      item.assignedUserId === user?.id || 
                      item.assignedChartingUserId === user?.id;
                    
                    return (
                    <tr 
                      key={item.id} 
                      className={`hover:bg-neutral-50 cursor-pointer ${
                        item.daysUntilExpiration !== undefined && item.daysUntilExpiration < 0
                          ? 'bg-red-50 border-l-4 border-red-300'
                          : isAssignedToCurrentUser ? 'bg-outfront-purple-light border-l-4 border-outfront-purple' : ''
                      }`}
                      onClick={(e) => {
                        // Don't open detail if clicking on interactive elements
                        const target = e.target as HTMLElement;
                        if (target.closest('button, select, a, [role="menu"], [data-no-row-click]')) return;
                        handleOpenDetailModal(item.id);
                      }}
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-400">
                        <div>{new Date(item.createdAt).toLocaleDateString('en-US', { timeZone: 'UTC' })}</div>
                        <div className="text-xs text-neutral-400">
                          {new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                        {item.requestNumber}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-900">
                        <div>{item.advertiser}</div>
                        {item.salesAeName && (
                          <div className="text-xs text-neutral-400">AE: {item.salesAeName}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-neutral-900">
                        <div>{item.displayName}</div>
                        <div className="flex items-center gap-0 text-xs text-neutral-400">
                          <span>
                            {item.flightStartDate && item.flightEndDate
                              ? `Flight: ${new Date(item.flightStartDate).toLocaleDateString('en-US', { timeZone: 'UTC' })} – ${new Date(item.flightEndDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}`
                              : 'Flight: dates unavailable'}
                          </span>
                          {item.commentCount != null && item.commentCount > 0 && (
                            <>
                              <span className="mx-1.5">•</span>
                              <span className="inline-flex items-center gap-0.5 text-outfront-purple" title={`${item.commentCount} comment${item.commentCount > 1 ? 's' : ''}`}>
                                <MessageCircle className="w-3.5 h-3.5" />
                                <span className="font-medium">{item.commentCount}</span>
                              </span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-neutral-900">
                        {item.qty ?? 1} / {item.digitalOnlyNoOfSpots != null ? item.digitalOnlyNoOfSpots : '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div>{new Date(item.holdExpirationDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}</div>
                        {item.chartingStatus === 'completed' ? (
                          <div className="text-xs text-green-600">Completed</div>
                        ) : (
                          <div className={`text-xs ${getExpirationColor(item.daysUntilExpiration)}`}>
                            {item.daysUntilExpiration !== undefined &&
                              (item.daysUntilExpiration < 0
                                ? `${Math.abs(item.daysUntilExpiration)} days overdue`
                                : item.daysUntilExpiration === 0
                                ? 'Expires today'
                                : `${item.daysUntilExpiration} days left`)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <StatusPill
                          status={item.chartingStatus}
                          onStatusChange={(newStatus, comment) => handleStatusChange(item, newStatus, comment)}
                          disabled={!user || !['charting_member', 'charting_manager', 'sales_vp', 'gvp', 'rvp', 'adops_leadership'].includes(user.role)}
                          requestNumber={item.requestNumber}
                          lineItemName={item.displayName}
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-900">
                        <div className="space-y-1">
                          {/* Line item assignment */}
                          <div className="flex items-center gap-2">
                            {item.assignedChartingUserName ? (
                              <div className="flex items-center gap-1">
                                <span className={item.assignedChartingUserId === user?.id ? 'font-semibold text-outfront-purple' : ''}>
                                  {item.assignedChartingUserName}
                                </span>
                                {item.assignedChartingUserId === user?.id && (
                                  <span className="text-xs bg-outfront-purple-light text-outfront-purple px-1 rounded">You</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-neutral-400 italic">Unassigned</span>
                            )}
                          </div>
                          {/* Assignment timestamp */}
                          {item.assignedAt && (
                            <div className="text-xs text-neutral-400">
                              Assigned: {new Date(item.assignedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end items-center gap-1">
                          {/* Primary action: Assign to Me (only when unassigned and not completed) */}
                          {!item.assignedChartingUserId && item.chartingStatus !== 'completed' && (
                            <Button
                              size="sm"
                              onClick={() => handleAssignToSelf(item)}
                              disabled={assignToSelf.isPending}
                              className="btn-outfront-primary"
                            >
                              Assign to Me
                            </Button>
                          )}

                          {/* Need Exception — visible for charting roles when not completed/already exception */}
                          {item.chartingStatus !== 'completed' && item.chartingStatus !== 'approval_required_by_exception' && user && ['charting_member', 'charting_manager', 'sales_vp', 'gvp', 'rvp', 'adops_leadership'].includes(user.role) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenActionModal(item, 'exception')}
                              disabled={requireException.isPending}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              Need Exception
                            </Button>
                          )}

                          {/* Overflow menu for additional actions — spacer for completed items to preserve layout */}
                          {item.chartingStatus === 'completed' ? (
                            <div className="w-8 h-8" />
                          ) : (() => {
                            const hasCompleteAction = item.assignedChartingUserId === user?.id && item.chartingStatus !== 'approval_required_by_exception';
                            const hasAssignAction = isManager;
                            const hasExceptionAction = false; // Removed from dropdown — already shown as row button
                            const hasAnyAction = hasCompleteAction || hasAssignAction;
                            
                            return (
                          <div className="relative" ref={openMenuId === item.id ? menuRef : undefined}>
                            <button
                              type="button"
                              onClick={() => {
                                if (!hasAnyAction) return;
                                setOpenMenuId(openMenuId === item.id ? null : item.id);
                              }}
                              className={`inline-flex items-center justify-center w-8 h-8 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple ${
                                hasAnyAction 
                                  ? 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 cursor-pointer' 
                                  : 'text-neutral-200 cursor-default'
                              }`}
                              aria-label="More actions"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                              </svg>
                            </button>
                            {openMenuId === item.id && (
                              <div className="absolute right-0 mt-1 w-48 bg-white rounded-md  border border-neutral-200 z-50 py-1">
                                {/* Complete — only if assigned to current user */}
                                {hasCompleteAction && (
                                  <button
                                    type="button"
                                    className="block w-full text-left px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100"
                                    disabled={markCompleted.isPending}
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      handleOpenActionModal(item, 'complete');
                                    }}
                                  >
                                    Mark Complete
                                  </button>
                                )}
                                {/* Manager assignment */}
                                {hasAssignAction && (
                                  <button
                                    type="button"
                                    className="block w-full text-left px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100"
                                    disabled={assignToUser.isPending}
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      handleOpenAssignModal(item);
                                    }}
                                  >
                                    {item.assignedChartingUserId ? 'Reassign' : 'Assign to Team'}
                                  </button>
                                )}
                                {/* Need Exception */}
                                {hasExceptionAction && (
                                  <button
                                    type="button"
                                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                    disabled={requireException.isPending}
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      handleOpenActionModal(item, 'exception');
                                    }}
                                  >
                                    Need Exception
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.total > 0 && (
            <div className="mt-4 flex items-center justify-between border-t border-neutral-200 pt-4">
              <div className="text-sm text-neutral-600">
                <span className="font-medium">{(filters.offset || 0) + 1}</span>
                {'-'}
                <span className="font-medium">{Math.min((filters.offset || 0) + (filters.limit || 50), pagination.total)}</span>
                {' of '}
                <span className="font-medium">{pagination.total}</span>
              </div>
              {pagination.total > (filters.limit || 50) && (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const newOffset = Math.max(0, (filters.offset || 0) - (filters.limit || 50));
                      const newFilters = { ...filters, offset: newOffset };
                      setFilters(newFilters);
                      saveFilters(newFilters);
                    }}
                    disabled={(filters.offset || 0) === 0}
                    className="btn-outfront-secondary"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const newOffset = (filters.offset || 0) + (filters.limit || 50);
                      const newFilters = { ...filters, offset: newOffset };
                      setFilters(newFilters);
                      saveFilters(newFilters);
                    }}
                    disabled={!pagination.hasMore}
                    className="btn-outfront-secondary"
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
          </div>
        </CardContent>
      </Card>

      {/* Action Modal */}
      <Modal isOpen={showActionModal && !!selectedItem} onClose={() => { setShowActionModal(false); setSelectedItem(null); setComments(''); }}>
        {selectedItem && (
          <div className="bg-white rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">
              {actionType === 'complete' ? 'Mark as Completed' : 'Require Exception Approval'}
            </h3>
            <div className="mb-4">
              <p className="text-sm text-neutral-600 mb-2">
                Request: {selectedItem.requestNumber}
              </p>
              <p className="text-sm text-neutral-600 mb-4">
                Line Item: {selectedItem.displayName}
              </p>
              <label className="block text-sm font-medium text-neutral-600 mb-2">
                {actionType === 'complete' ? 'Comments (optional)' : 'Reason (required)'}
              </label>
              <textarea
                className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple"
                rows={4}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder={
                  actionType === 'complete'
                    ? 'Add any notes about this completion...'
                    : 'Explain why exception approval is needed...'
                }
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowActionModal(false);
                  setSelectedItem(null);
                  setComments('');
                }}
                className="btn-outfront-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitAction}
                disabled={markCompleted.isPending || requireException.isPending}
                className="btn-outfront-primary"
              >
                {actionType === 'complete' ? 'Mark Complete' : 'Send for Approval'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Assignment Modal (Manager Only) */}
      <Modal isOpen={showAssignModal && !!selectedItem && isManager} onClose={() => { setShowAssignModal(false); setSelectedItem(null); setSelectedUserId(''); setSendNotification(true); }}>
        {selectedItem && (
          <div className="bg-white rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">
              Assign to Team Member
            </h3>
            <div className="mb-4">
              <p className="text-sm text-neutral-600 mb-2">
                Request: {selectedItem.requestNumber}
              </p>
              <p className="text-sm text-neutral-600 mb-2">
                Line Item: {selectedItem.displayName}
              </p>
              
              {/* Current assignment */}
              <div className="bg-neutral-50 p-3 rounded mb-4">
                <h4 className="text-sm font-medium text-neutral-600 mb-2">Current Assignment:</h4>
                <div className="text-sm text-neutral-600">
                  {selectedItem.assignedChartingUserName || 'Unassigned'}
                </div>
              </div>
              
              <label className="block text-sm font-medium text-neutral-600 mb-2">
                Assign to:
              </label>
              {isLoadingTeam ? (
                <div className="text-sm text-neutral-400">Loading team members...</div>
              ) : teamMembers && teamMembers.length > 0 ? (
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent cursor-pointer"
                  style={{ appearance: 'none', backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 4 5\'><path fill=\'%23666\' d=\'M2 0L0 2h4zm0 5L0 3h4z\'/></svg>")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '12px' }}
                >
                  <option value="">Select a team member...</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.firstName} {member.lastName} ({member.role === 'charting_manager' ? 'Manager' : 'Member'})
                    </option>
                  ))}
                </select>
              ) : (
                <div className="text-sm text-red-500">No team members available</div>
              )}
              
              {/* Notification options */}
              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={sendNotification}
                    onChange={(e) => setSendNotification(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-neutral-600">Send notification to assignee</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedItem(null);
                  setSelectedUserId('');
                  setSendNotification(true);
                }}
                className="btn-outfront-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignToUser}
                disabled={!selectedUserId || assignToUser.isPending}
                className="btn-outfront-primary"
              >
                Assign
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Line Item Detail Modal */}
      {showDetailModal && selectedLineItemId && (
        <LineItemDetail
          lineItemId={selectedLineItemId}
          onClose={handleCloseDetailModal}
          onAssignToSelf={async (lineItemId) => {
            try {
              const item = items.find(item => item.id === lineItemId);
              if (item) {
                await handleAssignToSelf(item);
              }
            } catch (error) {
              console.error('Failed to assign to self:', error);
            }
          }}
          onMarkCompleted={async (lineItemId, comments) => {
            try {
              await markCompleted.mutateAsync({
                lineItemId,
                ...(comments && { comments }),
              });
              handleCloseDetailModal();
            } catch (error) {
              console.error('Failed to mark completed:', error);
            }
          }}
          onRequireException={async (lineItemId, reason) => {
            try {
              await requireException.mutateAsync({
                lineItemId,
                reason,
              });
              handleCloseDetailModal();
            } catch (error) {
              console.error('Failed to require exception:', error);
            }
          }}
          onNavigateToLineItem={(lineItemId) => {
            setSelectedLineItemId(lineItemId);
          }}
          canAssign={!items.find(item => item.id === selectedLineItemId)?.assignedChartingUserId}
          canComplete={items.find(item => item.id === selectedLineItemId)?.chartingStatus !== 'completed'}
          isAssignedToUser={items.find(item => item.id === selectedLineItemId)?.assignedChartingUserId === user?.id}
        />
      )}
    </div>
  );
}
